import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TiptapDoc } from '@odo/shared-ui';
import {
    createNote,
    deleteNote,
    fetchNotes,
    updateNote,
    type PatientNote
} from '../services/patient/notes';
import { useToast } from '../providers/ToastProvider';

interface UsePatientNotesOptions {
    /** Skips the fetch until the Notes tab is actually opened. */
    enabled: boolean;
}

/**
 * Cache key for a patient's notes list.
 *
 * Exported because notes are written from two places: the Notes tab (this
 * file) and the appointment composer's chart-notes field (useAppointments).
 * Both have to invalidate the SAME key or a note saved from one surface
 * silently fails to appear on the other, so there must only ever be one
 * definition of it.
 */
export function notesQueryKey(patientId: string): ['patient', string, 'notes'] {
    return ['patient', patientId, 'notes'];
}

function extractErrorMessage(error: unknown, fallback: string): string {
    if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: unknown }).response === 'object'
    ) {
        const response = (error as { response?: { data?: { error?: { message?: string } } } })
            .response;
        return response?.data?.error?.message ?? fallback;
    }
    return fallback;
}

export function usePatientNotes(patientId: string | undefined, options: UsePatientNotesOptions) {
    const { data, isLoading, isFetching, isError, error } = useQuery({
        queryKey: notesQueryKey(patientId ?? ''),
        queryFn: () => fetchNotes(patientId!),
        enabled: options.enabled && !!patientId,
        staleTime: 60 * 1000
    });

    // `isFetching` (not just `isLoading`) matters to callers that need to know
    // a refetch is still in flight over a stale cache: `isLoading` is false as
    // soon as ANY cached data exists, even if it predates the note they are
    // waiting for.
    return { notes: data ?? [], isLoading, isFetching, isError, error };
}

interface CreateNoteData {
    title: string;
    bodyDoc: TiptapDoc;
}

export function useCreateNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (data: CreateNoteData) => createNote(patientId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notesQueryKey(patientId) });
            showToast('Note added', 'success');
        },
        onError: (error: unknown) => {
            showToast(extractErrorMessage(error, 'Failed to add note'), 'error');
        }
    });
}

interface UpdateNoteData {
    title?: string;
    bodyDoc?: TiptapDoc;
    pinned?: boolean;
}

interface UpdateNoteMutationVariables {
    noteId: string;
    data: UpdateNoteData;
}

interface UpdateNoteMutationContext {
    previous: PatientNote[] | undefined;
    /** Pin/unpin succeeds silently, but still reports failures — the optimistic
     *  row visibly reverts, so staying quiet would leave the user watching the
     *  pin flip back with no explanation. */
    isPinToggle: boolean;
}

export function useUpdateNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation<
        PatientNote,
        unknown,
        UpdateNoteMutationVariables,
        UpdateNoteMutationContext | undefined
    >({
        mutationFn: ({ noteId, data }) => updateNote(patientId, noteId, data),
        onMutate: async ({ noteId, data }) => {
            const isPinToggle = 'pinned' in data && Object.keys(data).length === 1;
            if (!isPinToggle) return undefined;

            await queryClient.cancelQueries({ queryKey: notesQueryKey(patientId) });
            const previous = queryClient.getQueryData<PatientNote[]>(notesQueryKey(patientId));

            if (previous) {
                queryClient.setQueryData<PatientNote[]>(
                    notesQueryKey(patientId),
                    previous.map((note) =>
                        note.id === noteId ? { ...note, pinned: data.pinned! } : note
                    )
                );
            }

            return { previous, isPinToggle };
        },
        onError: (error: unknown, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(notesQueryKey(patientId), context.previous);
            }
            const fallback = context?.isPinToggle
                ? 'Failed to update pin'
                : 'Failed to update note';
            showToast(extractErrorMessage(error, fallback), 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: notesQueryKey(patientId) });
        }
    });
}

export function useDeleteNote(patientId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (noteId: string) => deleteNote(patientId, noteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notesQueryKey(patientId) });
            showToast('Note deleted', 'success');
        },
        onError: (error: unknown) => {
            showToast(extractErrorMessage(error, 'Failed to delete note'), 'error');
        }
    });
}
